import {
    Breadcrumbs,
    Button,
    Grid,
    IconButton, Paper, Skeleton,
    Stack,
    Switch, Table, TableBody,
    TableCell,
    TableContainer, TableHead,
    TableRow, Tooltip,
    Typography
} from "@mui/material";
import {
    ChainIcon,
    DownloadIcon,
    EditIcon,
    HomeIcon,
    SaveIcon,
    SystemInformationIcon,
    TrashIcon
} from "../../../common/Icons";
import {useCommonItems} from "../../../../contexts/CommonItensProvider";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useSearchParams} from "react-router-dom";
import React, {useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {
    atualizaChangelog,
    atualizaSistema,
    deletaChangelog,
    deletaSistema, downloadChangelog, enviaChangelog,
    listaChangelogs
} from "../configs/functions";
import {Helmet} from "react-helmet-async";
import LinkNavegacao from "../../../common/Link";
import PageTitle from "../../../common/PageTitle";
import {FiltersButton} from "../../../common/FiltersButton";
import {Add, Download, Email, RemoveRedEye} from "@mui/icons-material";
import {ChangelogsTableFilter} from "./ChangelogsTableFilter";
import {SkeletonTableRow} from "../../../common/SkeletonTableRow";
import {EmptyTableRow} from "../../../common/EmptyTableRow";
import {Pagination} from "../../../common/Pagination";
import ConfirmDialog from "../../../common/ConfirmDialog";
import {ChangelogForm} from "./forms/ChangelogForm";
import {blue, grey} from "@mui/material/colors";
import {NotificationCard} from "../../../common/NotificationCard";


const ChangelogRow = ({
                          data,
                          handlePreview,
                          handleOpenEditForm,
                          handleUpdateStatus,
                          handleOpenDeleteDialog,
                          handleSendChangelog,
                          handleDownloadFile,
                          sendingIds
}) => {
    return (
        <>
            <TableRow hover>
                <TableCell>{data.nome}</TableCell>
                <TableCell
                    sx={{
                        maxWidth: 400,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {data.descricao}
                </TableCell>
                <TableCell>{data.tipo}</TableCell>
                <TableCell>{data.versao}</TableCell>
                <TableCell>{data.observacao}</TableCell>
                <TableCell>{data.data}</TableCell>
                <TableCell>
                    <Switch
                        checked={!!data.status}
                        onChange={handleUpdateStatus}
                    />
                </TableCell>
                <TableCell sx={{ minWidth: 50 }}>
                    <IconButton aria-label="Editar" onClick={() => handleOpenEditForm(data)}>
                        <EditIcon />
                    </IconButton>

                    <Tooltip
                        title={data.arquivo ? "Baixar arquivo" : "Nenhum arquivo disponível para download"}
                    >
                      <span>
                        <IconButton
                            aria-label="Download"
                            disabled={!data.arquivo}
                            onClick={() => handleDownloadFile(data)}
                        >
                          <Download />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip
                        title={
                            data.status
                                ? (data.enviado === 1 ? "Notificação já enviada" : "Enviar Notificação")
                                : (data.enviado === 1 ? "Notificação já enviada" : "Alteração desabilitada")
                        }
                    >
                      <span>
                        <IconButton
                            aria-label="Enviar"
                            disabled={data.enviado || !data.status || sendingIds.includes(data.id)}
                            onClick={() => handleSendChangelog(data)}
                        >
                          <Email />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <IconButton
                        aria-label="Excluir"
                        disabled={!data.status}
                        onClick={() => handleOpenDeleteDialog(data)}
                    >
                        <TrashIcon />
                    </IconButton>

                    <IconButton aria-label="Preview" onClick={() => handlePreview()}>
                        <RemoveRedEye />
                    </IconButton>
                </TableCell>
            </TableRow>
        </>
    );
}

export function ListOfChangelogs() {
    const { exibirAlerta } = useCommonItems();

    const queryClient = useQueryClient();

    const [searchParams, setSearchParams] = useSearchParams();

    const searchParamPage = searchParams.get('page');
    const searchParamPerPage = searchParams.get('perPage');

    // estados para controle de paginacao
    const page = searchParamPage ? (parseInt(searchParamPage) - 1) : 0;
    const rowsPerPage = searchParamPerPage ? parseInt(searchParamPerPage) : 10;

    const [selectedChangelog, setSelectedChangelog] = useState(null);
    const [openForm, setOpenForm] = useState(false);
    const [deleteOpenDialog, setOpenDeleteDialog] = useState(false);

    const [previewOpen, setPreviewOpen] = useState(false);

    const [sendingIds, setSendingIds] = useState([]);

    const [isOpenFilters, setIsOpenFilters] = useState(false);
    const [activeFilters, setActiveFilters] = useState(0);

    const queryFilters = {
        nome: searchParams.get('nome'),
        tipo: searchParams.get('tipo'),
        data_inicio: searchParams.get('data_inicio'),
        data_fim: searchParams.get('data_fim'),
        limit: rowsPerPage,
        offset: page * rowsPerPage,
    };

    const queryKey = ['listaAlteracoes', queryFilters];
    const { data: listaChangelogsData, isPending: listaChangelogsDataIsLoading } = useQuery({
        queryKey,
        queryFn: () => listaChangelogs(queryFilters)
    });

    const changelogs = listaChangelogsData?.data?.data ?? [];
    const numTotalItems = listaChangelogsData?.data?.data?.numero_total ?? 0;

    const handleEdit = (item) => {
        setSelectedChangelog(item);
        setOpenForm(true);
    };

    const handleOpenDeleteDialog = (item) => {
        setSelectedChangelog(item);
        setOpenDeleteDialog(true);
    };

    const handleUpdateStatus = useMutation({
        mutationFn: (data) => atualizaChangelog(data.id, data.dataToUpdate),
        onSuccess: (response) => {
            const message = response?.data?.message ?? 'Status atualizado com sucesso';
            exibirAlerta('Sucesso', message, 'success');
            queryClient.invalidateQueries({ queryKey });
        },
        onError: (error) => {
            const message = error.response?.data?.message ?? 'Erro ao executar operação';
            exibirAlerta('Erro', message, 'error');
        }
    });

    const handleSendChangelog = useMutation({
        mutationFn: (data) => enviaChangelog({ id_changelog: data.id }),
        onMutate: (data) => {
            setSendingIds(prev => [...prev, data.id]);
        },
        onSuccess: (response) => {
            const message = response.data?.message ?? 'Notificação enviada com sucesso';
            exibirAlerta('Sucesso', message, 'success');

            setTimeout(() => {
                window.location.reload();
            }, 3000);
        },
        onError: (error) => {
            const message = error.response?.data.message ?? 'Erro ao executar operação';
            exibirAlerta('Erro', message, 'error');
        }
    });

    const { mutateAsync: handleDelete } = useMutation({
        mutationFn: deletaChangelog,
        onSuccess: (response, id) => {
            const message = response.data?.message ?? 'Alteração excluída com sucesso';
            exibirAlerta('Sucesso', message, 'success');
        },
        onError: (error) => {
            const message = error.response?.data.message ?? 'Erro ao executar operação';
            exibirAlerta('Erro', message, 'error');
        },
        onSettled: () => {
            setOpenDeleteDialog(false);
            setSelectedChangelog(null);
        }
    });

    async function handleDownloadFile(item) {
        try {
            const res = await downloadChangelog(item.id);

            if (res.data?.data) {
                window.open(res.data.data, "_blank");
            } else {
                exibirAlerta('Erro', 'Arquivo não encontrado', 'error');
            }
        } catch (error) {
            const message = error.response?.data?.message ?? 'Erro ao baixar arquivo';
            exibirAlerta('Erro', message, 'error');
        }
    }

    useEffect(() => {
        if (!openForm) setSelectedChangelog(null);
    }, [openForm]);

    useEffect(() => {
        setSearchParams((state) => {
            state.set('page', String(page + 1));
            state.set('perPage', String(rowsPerPage));

            return state;
        });
    }, [page, rowsPerPage, setSearchParams]);

    useEffect(() => {
        const queryFilters = {
            ...Object.fromEntries(searchParams),
        }

        const tableFilters = { ...queryFilters };
        delete tableFilters.page;
        delete tableFilters.perPage;

    }, [searchParams]);

    return (
        <>
            <Helmet title="Alterações" defer={false} />

            <Grid container direction="row" justifyContent="flex-start" alignItems="stretch" spacing={3}>
                <Grid item xs={12} md={9}>
                    <Breadcrumbs>
                        <LinkNavegacao to='/'><HomeIcon fontSize='small' /></LinkNavegacao>
                        <Typography variant='span'>Admin Sempher</Typography>
                        <LinkNavegacao to='/admin/operacional'>Gestão Operacional</LinkNavegacao>
                        <Typography variant='span'>Gerenciamento de Alterações</Typography>
                    </Breadcrumbs>

                    <PageTitle
                        icon={<SaveIcon fontSize='large' />}
                        title="Gerenciamento de Alterações"
                        description="Gerencie as alterações realizadas nos sistemas oferecidos"
                    />
                </Grid>

                <Grid item xs={12}>
                    <Stack direction={{ xs: 'column-reverse', md: 'row' }} spacing={2} justifyContent={'space-between'}>
                        <FiltersButton
                            isOpenFilters={isOpenFilters}
                            setIsOpenFilters={setIsOpenFilters}
                            activeFilters={activeFilters}
                        />

                        <Typography sx={{ textAlign: { xs: 'center', md: 'right' }, mb: 1 }}>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => {
                                    setSelectedChangelog(null);
                                    setOpenForm(true);
                                }}
                            >
                                Cadastrar Alteração
                            </Button>
                        </Typography>
                    </Stack>
                </Grid>

                <ChangelogsTableFilter
                    isOpenFilters={isOpenFilters}
                    isLoadingList={listaChangelogsDataIsLoading}
                />

                <Grid item xs={12}>
                    <TableContainer component={Paper}>
                        <Table aria-label="Alterações">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Nome</TableCell>
                                    <TableCell>Descrição</TableCell>
                                    <TableCell>Tipo</TableCell>
                                    <TableCell>Versão</TableCell>
                                    <TableCell>Observação</TableCell>
                                    <TableCell>Data</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {listaChangelogsDataIsLoading ? (
                                    <SkeletonTableRow numCells={8} numRows={rowsPerPage < 20 ? rowsPerPage : 5} />
                                ) : changelogs.length ? (
                                    changelogs.map((item) => (
                                        <ChangelogRow
                                            key={item.id}
                                            data={item}
                                            handlePreview={() => {
                                                setSelectedChangelog(item);
                                                setPreviewOpen(true);
                                            }}
                                            handleUpdateStatus={() => {
                                                const dataSendUpdate = {
                                                    id: item.id,
                                                    dataToUpdate: {
                                                        status: `${+!item.status}`,
                                                        enviado: `0`
                                                    }
                                                };
                                                handleUpdateStatus.mutateAsync(dataSendUpdate)
                                            }}
                                            handleOpenEditForm={() => handleEdit(item)}
                                            handleOpenDeleteDialog={() => handleOpenDeleteDialog(item)}
                                            handleSendChangelog={() => handleSendChangelog.mutateAsync(item)}
                                            handleDownloadFile={() => handleDownloadFile(item)}
                                            sendingIds={sendingIds}
                                        />
                                    ))
                                ) : (
                                    <EmptyTableRow colSpan={8} />
                                )}
                            </TableBody>
                        </Table>

                        <Pagination
                            numTotalItems={numTotalItems}
                            rowsPerPage={rowsPerPage}
                            page={page}
                        />
                    </TableContainer>
                </Grid>
            </Grid>

            <ChangelogForm
                open={openForm}
                setOpen={(value) => {
                    setOpenForm(value);
                    if (!value) setSelectedChangelog(null);
                }}
                previewOpen={previewOpen}
                setPreviewOpen={setPreviewOpen}
                changelog={selectedChangelog}
                queryKey={queryKey}
            />

            <ConfirmDialog
                description="Tem certeza que deseja excluir essa alteração?"
                title="Excluir Alteração"
                goAction={() => handleDelete(selectedChangelog.id)}
                handleClose={() => {
                    setOpenDeleteDialog(false);
                    setSelectedChangelog(null);
                }}
                state={deleteOpenDialog}
            />
        </>
    );
}